import moment from 'moment';
import getLegacyDaypartString, {
  getLegacyDaypartStringTimeOnly,
} from './getLegacyDaypartString';

export const getDaypartString = ({
  dayparts,
  daypartSlots,
  dayInFull,
}: {
  dayparts?: DaypartIds;
  daypartSlots: DaypartMap;
  dayInFull?: boolean;
}): string => {
  if (!dayparts || dayparts.length === 0) return '';

  const availabilityStrings: string[] = [];

  for (const daypartId of dayparts) {
    const daypartConfig = daypartSlots[daypartId];
    if (!daypartConfig) continue;

    const { days, start_time, end_time } = daypartConfig;

    if (!start_time || !end_time) continue;

    const timeString = getLegacyDaypartString(
      true,
      days,
      start_time,
      end_time,
      dayInFull,
    );
    if (timeString) {
      availabilityStrings.push(timeString);
    }
  }

  return availabilityStrings.join(' or ');
};

export const getDaypartStringTimeOnly = ({
  dayparts,
  daypartSlots,
}: {
  dayparts?: DaypartIds;
  daypartSlots: DaypartMap;
}): string => {
  if (!dayparts || dayparts.length === 0) return '';

  const availabilityStrings: string[] = [];

  for (const daypartId of dayparts) {
    const daypartConfig = daypartSlots[daypartId];
    if (!daypartConfig) continue;

    const { start_time, end_time } = daypartConfig;

    if (!start_time || !end_time) continue;

    const timeString = getLegacyDaypartStringTimeOnly(
      true,
      start_time,
      end_time,
    );
    if (timeString) {
      availabilityStrings.push(timeString);
    }
  }

  return availabilityStrings.join(' or ');
};

export const getDaypartAvailability = ({
  dayparts,
  daypartSlots,
  dateTimeSelected,
}: {
  dayparts?: DaypartIds;
  daypartSlots: DaypartMap;
  dateTimeSelected?: string;
}): boolean => {
  if (!dayparts || dayparts.length === 0) return true;

  const selectedMoment = moment(dateTimeSelected);
  const selectedWeekDay = selectedMoment.format('dddd').toUpperCase();
  const selectedTime = selectedMoment.format('HH:mm');

  for (const daypartId of dayparts) {
    const daypartConfig = daypartSlots[daypartId];
    if (!daypartConfig) continue;

    const { days, start_time, end_time } = daypartConfig;
    const daysUppercased = days.map(day => day.toString().toUpperCase());

    if (
      daysUppercased.length > 0 &&
      !daysUppercased.includes(selectedWeekDay)
    ) {
      continue;
    }

    const isOvernightPeriod = start_time > end_time;

    if (isOvernightPeriod) {
      if (selectedTime >= start_time || selectedTime <= end_time) {
        return true;
      }
    } else {
      if (selectedTime >= start_time && selectedTime <= end_time) {
        return true;
      }
    }
  }

  return false;
};

export const isUsingLegacyDayparts = (
  dayparts: DaypartIds | undefined,
): boolean => {
  return !dayparts || dayparts.length === 0;
};

export const checkCategoryAvailability = ({
  category,
  dayInFull,
  dateTimeSelected,
  daypartSlots = {},
}: {
  category: Partial<Category | CategoryInfo> | undefined;
  dayInFull?: boolean;
  dateTimeSelected?: string;
  daypartSlots?: DaypartMap;
}): [boolean, string] => {
  if (!category) return [true, ''];

  const {
    dayparts,

    // Legacy fields below:
    // to be removed once all clients are migrated to dayparts.
    availability,
    days,
    start,
    end,
  } = category;

  if (isUsingLegacyDayparts(dayparts)) {
    const legacyIsAvailable = getLegacyDaypartsAvailability({
      availability: Boolean(availability),
      days,
      start,
      end,
      dateTimeSelected,
    });

    const legacyDaypartString = legacyIsAvailable
      ? getLegacyDaypartStringTimeOnly(Boolean(availability), start, end)
      : getLegacyDaypartString(
          Boolean(availability),
          days,
          start,
          end,
          dayInFull,
        );

    return [legacyIsAvailable, legacyDaypartString];
  }

  const isAvailable = getDaypartAvailability({
    dayparts,
    daypartSlots,
    dateTimeSelected,
  });
  const daypartString = isAvailable
    ? getDaypartStringTimeOnly({
        dayparts,
        daypartSlots,
      })
    : getDaypartString({
        dayparts,
        daypartSlots,
        dayInFull,
      });
  return [isAvailable, daypartString];
};

export const getLegacyDaypartsAvailability = ({
  availability,
  days = [],
  start = '',
  end = '',
  timeBracket = 0,
  dateTimeSelected,
}: {
  availability: boolean;
  days?: Day[];
  start?: string | null;
  end?: string | null;
  timeBracket?: number;
  dateTimeSelected?: string;
}): boolean => {
  if (!availability) return true;
  if (!(days.length > 0) && !start && !end) return true;

  const selectedWeekDay = moment(dateTimeSelected).format('dddd').toUpperCase();
  const daysUppercased = days.map(day => day.toString().toUpperCase());

  const date = `${moment(dateTimeSelected).format('yyyy-MM-DD')}`;

  // Check if selected weekday
  if (daysUppercased.length > 0 && !daysUppercased.includes(selectedWeekDay)) {
    return false;
  }

  if (start && moment(`${date} ${start}`).diff(moment(dateTimeSelected)) > 0)
    return false;

  if (
    end &&
    moment(dateTimeSelected).diff(moment(`${date} ${end}`), 'minute') >
      timeBracket
  ) {
    return false;
  }

  return true;
};
